import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

import {
  parseAdminGroups,
  resolveAdminRole,
  type AdminRole,
} from '#shared/adminPermissions';
import type { RuntimeConfig } from '#src/config';
import { hasLocalAdminSession, LOCAL_ADMIN_TOKEN } from '#src/utils/localAdmin';
import { isLocalDemoMode } from '#src/utils/localDemo';

const TOKEN_KEY = 'amsleighpfa_admin_tokens';

let cognitoConfig: RuntimeConfig | null = null;
let pendingUser: CognitoUser | null = null;
let pendingResetUser: CognitoUser | null = null;

export const setCognitoConfig = (config: RuntimeConfig): void => {
  cognitoConfig = config;
};

const requireConfig = (): RuntimeConfig => {
  if (!cognitoConfig) {
    throw new Error('Cognito not configured');
  }
  return cognitoConfig;
};

interface TokenSet {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
  username: string;
}

export type LoginChallenge =
  { type: 'newPasswordRequired' } | { type: 'mfaRequired' };

export type LoginResult =
  { status: 'success' } | { status: 'challenge'; challenge: LoginChallenge };

export const getStoredTokens = (): TokenSet | null => {
  const raw = sessionStorage.getItem(TOKEN_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as TokenSet;
  } catch {
    return null;
  }
};

export const clearTokens = (): void => {
  sessionStorage.removeItem(TOKEN_KEY);
};

const storeTokens = (tokens: TokenSet): void => {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
};

const getUserPool = (): CognitoUserPool => {
  const config = requireConfig();
  return new CognitoUserPool({
    UserPoolId: config.cognitoUserPoolId,
    ClientId: config.cognitoClientId,
  });
};

const sessionToTokens = (
  session: CognitoUserSession,
  username: string,
): TokenSet => ({
  accessToken: session.getAccessToken().getJwtToken(),
  idToken: session.getIdToken().getJwtToken(),
  refreshToken: session.getRefreshToken().getToken(),
  expiresAt: session.getAccessToken().getExpiration() * 1000,
  username,
});

const saveSession = (session: CognitoUserSession, username: string): void => {
  storeTokens(sessionToTokens(session, username));
};

const authCallbacks = (
  username: string,
  resolve: (result: LoginResult) => void,
  reject: (error: Error) => void,
) => ({
  onSuccess: (session: CognitoUserSession) => {
    pendingUser = null;
    saveSession(session, username);
    resolve({ status: 'success' });
  },
  onFailure: (err: Error) => {
    pendingUser = null;
    reject(err);
  },
  newPasswordRequired: () => {
    resolve({
      status: 'challenge',
      challenge: { type: 'newPasswordRequired' },
    });
  },
  mfaRequired: () => {
    resolve({
      status: 'challenge',
      challenge: { type: 'mfaRequired' },
    });
  },
  totpRequired: () => {
    resolve({
      status: 'challenge',
      challenge: { type: 'mfaRequired' },
    });
  },
});

export const login = (email: string, password: string): Promise<LoginResult> =>
  new Promise((resolve, reject) => {
    const username = email.trim();
    const pool = getUserPool();
    const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
    pendingUser = cognitoUser;

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    cognitoUser.authenticateUser(
      authDetails,
      authCallbacks(username, resolve, reject),
    );
  });

export const completeNewPassword = (
  newPassword: string,
): Promise<LoginResult> =>
  new Promise((resolve, reject) => {
    if (!pendingUser) {
      reject(new Error('No pending login'));
      return;
    }

    const username = pendingUser.getUsername();
    pendingUser.completeNewPasswordChallenge(
      newPassword,
      {},
      authCallbacks(username, resolve, reject),
    );
  });

export const confirmMfa = (code: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (!pendingUser) {
      reject(new Error('No pending login'));
      return;
    }

    const username = pendingUser.getUsername();
    pendingUser.sendMFACode(code.trim(), {
      onSuccess: (session) => {
        pendingUser = null;
        saveSession(session, username);
        resolve();
      },
      onFailure: (err) => {
        pendingUser = null;
        reject(err);
      },
    });
  });

export const refreshSessionIfNeeded = (): Promise<string | null> => {
  const tokens = getStoredTokens();
  if (!tokens) {
    return Promise.resolve(null);
  }
  if (Date.now() < tokens.expiresAt - 60_000) {
    return Promise.resolve(tokens.accessToken);
  }
  if (!tokens.refreshToken || !tokens.username) {
    clearTokens();
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const pool = getUserPool();
    const cognitoUser = new CognitoUser({
      Username: tokens.username,
      Pool: pool,
    });
    const refreshToken = new CognitoRefreshToken({
      RefreshToken: tokens.refreshToken,
    });

    cognitoUser.refreshSession(refreshToken, (err, session) => {
      if (err || !session) {
        clearTokens();
        resolve(null);
        return;
      }
      saveSession(session, tokens.username);
      resolve(session.getAccessToken().getJwtToken());
    });
  });
};

export const getAccessToken = (): string | null => {
  if (isLocalDemoMode() && hasLocalAdminSession()) {
    return LOCAL_ADMIN_TOKEN;
  }

  const tokens = getStoredTokens();
  if (!tokens) {
    return null;
  }
  if (Date.now() >= tokens.expiresAt - 60_000) {
    return null;
  }
  return tokens.accessToken;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const getAccessTokenClaims = (): Record<string, unknown> | null => {
  const token = getAccessToken();
  if (!token) {
    return null;
  }
  return decodeJwtPayload(token);
};

export const getAdminGroupsFromToken = (): AdminRole[] => {
  const claims = getAccessTokenClaims();
  if (!claims) {
    return [];
  }
  return parseAdminGroups(claims['cognito:groups'] as string[] | string);
};

export const getAdminRole = (): AdminRole | null =>
  resolveAdminRole(getAdminGroupsFromToken());

export const getAdminSubject = (): string | null => {
  const claims = getAccessTokenClaims();
  return typeof claims?.sub === 'string' ? claims.sub : null;
};

const claimString = (
  claims: Record<string, unknown> | null,
  key: string,
): string | null => {
  const value = claims?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

const looksLikeEmail = (value: string): boolean => value.includes('@');

/**
 * Resolve the signed-in admin's email for display.
 * Cognito access tokens often put the internal user id in `username` when the
 * pool uses email as a sign-in alias — prefer the ID token `email` claim.
 */
export const getAdminEmail = (): string | null => {
  const tokens = getStoredTokens();

  const idClaims = tokens?.idToken ? decodeJwtPayload(tokens.idToken) : null;
  const idEmail = claimString(idClaims, 'email');
  if (idEmail) {
    return idEmail;
  }

  const accessClaims = getAccessTokenClaims();
  const accessEmail = claimString(accessClaims, 'email');
  if (accessEmail) {
    return accessEmail;
  }

  // Login stores the identifier the user typed (their email).
  if (tokens?.username && looksLikeEmail(tokens.username)) {
    return tokens.username;
  }

  return null;
};

export const logout = (): void => {
  pendingUser = null;
  pendingResetUser = null;
  clearTokens();
};

export const requestPasswordReset = (email: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const username = email.trim();
    const pool = getUserPool();
    const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
    pendingResetUser = cognitoUser;

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        pendingResetUser = null;
        reject(err);
      },
    });
  });

export const confirmPasswordReset = (
  verificationCode: string,
  newPassword: string,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (!pendingResetUser) {
      reject(new Error('No pending password reset'));
      return;
    }

    pendingResetUser.confirmPassword(verificationCode.trim(), newPassword, {
      onSuccess: () => {
        pendingResetUser = null;
        resolve();
      },
      onFailure: (err) => {
        pendingResetUser = null;
        reject(err);
      },
    });
  });

export const cancelPasswordReset = (): void => {
  pendingResetUser = null;
};

export const formatCognitoError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message || 'Sign in failed';
  }
  return 'Sign in failed';
};
